import {
  ApiChartRank,
  ApiChartRankDrection,
  ApiComponentType,
  ApiMetric,
  ApiProduct,
  ApiSimpleComponent,
} from "@incendium/api";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import type { Editor } from "grapesjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelectedProject } from "Hooks";
import { useLanderBlocks } from "features/lightningLanders/hooks/useLanderBlocks";
import {
  DEFAULT_LAST_N_DAYS,
  CUSTOM_TOP_PRODUCTS_METRIC_OPTIONS,
  LAST_N_DAYS_OPTIONS,
  TOP_PRODUCTS_STRATEGIES,
  useTopProductsFromAnalytics,
  type TopProductsStrategyKey,
} from "features/lightningLanders/hooks/useTopProductsFromAnalytics";
import { useProducts } from "Hooks/useProducts";
import {
  buildProductGridHtml,
  type ProductGridConfig,
  type ProductGridConfigSource,
  type ProductGridOptions,
} from "features/lightningLanders/utils/dynamicBlockGrid";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { useLanderLandingComponents } from "../hooks/useLanderLandingComponents";

export type ProductSourceType = "manual" | "custom" | TopProductsStrategyKey;

const PRODUCT_SOURCE_OPTIONS: { value: ProductSourceType; label: string }[] = [
  { value: "manual", label: "Select products manually" },
  { value: "custom", label: "Custom ranking" },
  ...(
    Object.entries(TOP_PRODUCTS_STRATEGIES) as Array<
      [
        TopProductsStrategyKey,
        (typeof TOP_PRODUCTS_STRATEGIES)[TopProductsStrategyKey],
      ]
    >
  ).map(([key, strategy]) => ({
    value: key,
    label: strategy.label,
  })),
];

const TOP_N_OPTIONS = [3, 5, 6, 8, 10, 12, 15, 20] as const;
const COLUMNS_DESKTOP_OPTIONS = [2, 3, 4, 5, 6] as const;
const DEFAULT_COLUMNS_DESKTOP = 4;

export type ProductGridInsertCallback = (html: string, css: string) => void;

interface DynamicProductGridModalProps {
  open: boolean;
  onClose: () => void;
  onInsert?: ProductGridInsertCallback;
  editor?: Editor | null;
  initialConfig?: ProductGridConfig | null;
}

const DEFAULT_TOP_N = 10;
const DEFAULT_CUSTOM_RANKS: ApiChartRank[] = [
  {
    metric: ApiMetric.METRIC_PRODUCT_REVENUE,
    direction: ApiChartRankDrection.DESC,
    weight: 0.8,
  },
  {
    metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
    direction: ApiChartRankDrection.DESC,
    weight: 0.2,
  },
];

function toProductSourceType(source?: string): ProductSourceType {
  if (source === "top_pageviews") return "highly_clicked_products";
  if (source === "top_revenue") return "top_sellers";
  if (source === "custom") return "custom";
  if (!source || source === "manual") return "manual";
  return source in TOP_PRODUCTS_STRATEGIES
    ? (source as TopProductsStrategyKey)
    : "manual";
}

export default function DynamicProductGridModal({
  open,
  onClose,
  onInsert,
  editor: editorProp,
  initialConfig,
}: DynamicProductGridModalProps) {
  const editor = editorProp;
  const { selectedProject } = useSelectedProject();
  const { blocks: landerBlocks } = useLanderBlocks();
  const { components: landingComponents } = useLanderLandingComponents();
  const { products, loading: productsLoading } = useProducts();
  const { fetchTopProductNames } = useTopProductsFromAnalytics();

  const [selectedBlock, setSelectedBlock] = useState<ApiSimpleComponent | null>(
    null,
  );
  const [productSource, setProductSource] =
    useState<ProductSourceType>("manual");
  const [topN, setTopN] = useState<number>(DEFAULT_TOP_N);
  const [lastNDays, setLastNDays] = useState<number>(DEFAULT_LAST_N_DAYS);
  const [customRanks, setCustomRanks] =
    useState<ApiChartRank[]>(DEFAULT_CUSTOM_RANKS);
  const [selectedProducts, setSelectedProducts] = useState<ApiProduct[]>([]);
  const [columnsDesktop, setColumnsDesktop] = useState<number>(
    DEFAULT_COLUMNS_DESKTOP,
  );
  const [submitting, setSubmitting] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  const blocks = useMemo(() => {
    return [...landerBlocks, ...landingComponents];
  }, [landerBlocks, landingComponents]);

  useEffect(() => {
    if (!open) return;
    setAlertMessage(null);
    if (initialConfig) {
      const block = blocks.find((b) => b.id === initialConfig.blockId) ?? null;
      const prods = (initialConfig.productIds ?? [])
        .map((id) => products.find((p) => p.id === id))
        .filter(Boolean) as ApiProduct[];
      setSelectedBlock(block);
      const source = toProductSourceType(initialConfig.productSource);
      setProductSource(source);
      setTopN(
        initialConfig.topN &&
          TOP_N_OPTIONS.includes(
            initialConfig.topN as (typeof TOP_N_OPTIONS)[number],
          )
          ? initialConfig.topN
          : DEFAULT_TOP_N,
      );
      setLastNDays(
        initialConfig.lastNDays &&
          LAST_N_DAYS_OPTIONS.includes(
            initialConfig.lastNDays as (typeof LAST_N_DAYS_OPTIONS)[number],
          )
          ? initialConfig.lastNDays
          : DEFAULT_LAST_N_DAYS,
      );
      setCustomRanks(
        source === "custom" && initialConfig.customRanks?.length
          ? initialConfig.customRanks
          : DEFAULT_CUSTOM_RANKS,
      );
      setSelectedProducts(prods);
      setColumnsDesktop(
        initialConfig.columnsDesktop ?? DEFAULT_COLUMNS_DESKTOP,
      );
    } else {
      setSelectedBlock(null);
      setProductSource("manual");
      setTopN(DEFAULT_TOP_N);
      setLastNDays(DEFAULT_LAST_N_DAYS);
      setCustomRanks(DEFAULT_CUSTOM_RANKS);
      setSelectedProducts([]);
      setColumnsDesktop(DEFAULT_COLUMNS_DESKTOP);
    }
  }, [open, initialConfig, blocks, products]);

  const handleClose = useCallback(() => {
    setAlertMessage(null);
    setSelectedBlock(null);
    setProductSource("manual");
    setTopN(DEFAULT_TOP_N);
    setLastNDays(DEFAULT_LAST_N_DAYS);
    setCustomRanks(DEFAULT_CUSTOM_RANKS);
    setSelectedProducts([]);
    setColumnsDesktop(DEFAULT_COLUMNS_DESKTOP);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    const isTopBy = productSource !== "manual";
    const isCustomRanking = productSource === "custom";
    let productsToUse = selectedProducts;

    if (isTopBy) {
      if (!selectedBlock || !selectedProject?.id) return;
      if (!editor && !onInsert) return;

      setSubmitting(true);
      try {
        const names = await fetchTopProductNames(
          selectedProject.id,
          isCustomRanking ? null : (productSource as TopProductsStrategyKey),
          topN,
          lastNDays,
          isCustomRanking ? customRanks : undefined,
        );
        if (names.length === 0) {
          setSubmitting(false);
          setAlertMessage(
            "Analytics returned no products. Try a different date range or select products manually.",
          );
          return;
        }
        productsToUse = names
          .map((name) => products.find((p) => (p.name ?? "") === name))
          .filter((p): p is ApiProduct => p != null);
      } catch {
        setSubmitting(false);
        setAlertMessage(
          "Failed to load top products from analytics. Try again or select products manually.",
        );
        return;
      }
    } else {
      if (!selectedBlock || selectedProducts.length === 0) return;
      if (!editor && !onInsert) return;
    }

    setSubmitting(true);
    try {
      const count = productsToUse.length;
      const config: ProductGridConfig = {
        blockId: selectedBlock!.id!,
        productIds: productsToUse.map((p) => p.id!).filter(Boolean),
        productCount: count,
        columnsDesktop,
        ...(initialConfig?.uniqueId && { uniqueId: initialConfig.uniqueId }),
        ...(isTopBy && {
          productSource: productSource as ProductGridConfigSource,
          topN,
          lastNDays,
          ...(isCustomRanking && { customRanks }),
        }),
      };
      const gridOptions: ProductGridOptions = {
        maxProducts: count,
        columnsDesktop,
        config,
      };
      const { html, css } = buildProductGridHtml(
        selectedBlock!,
        productsToUse,
        gridOptions,
      );

      if (onInsert) {
        onInsert(html, css);
      } else if (editor) {
        editor.addComponents(html);
        editor.UndoManager.skip(() => {
          editor.Css.addRules(css);
        });
      }

      handleClose();
    } finally {
      setSubmitting(false);
    }
  }, [
    editor,
    initialConfig?.uniqueId,
    selectedBlock,
    selectedProducts,
    productSource,
    selectedProject?.id,
    topN,
    lastNDays,
    customRanks,
    columnsDesktop,
    handleClose,
    onInsert,
    fetchTopProductNames,
    products,
  ]);

  const canConfirm =
    !!selectedBlock &&
    !submitting &&
    (productSource === "manual"
      ? selectedProducts.length > 0
      : productSource === "custom"
        ? customRanks.length > 0 &&
          customRanks.every(
            (rank) =>
              !!rank.metric &&
              typeof rank.weight === "number" &&
              Number(rank.weight) > 0,
          )
        : true);

  return (
    <CustomDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add product grid</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose a block to use as the product card template and select which
          products to show. The block should have elements mapped to variables
          (e.g. product_name, product_price, product_image) in block settings.
        </Typography>

        {alertMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setAlertMessage(null)}
          >
            {alertMessage}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Block / Component (template)"
            value={selectedBlock?.id ?? ""}
            onChange={(e) => {
              const id = Number(e.target.value);
              const block = blocks.find((b) => b.id === id) ?? null;
              setSelectedBlock(block);
            }}
          >
            <MenuItem value="">Select a block</MenuItem>
            {blocks.map((block) => (
              <MenuItem key={block.id} value={block.id}>
                {block.name ?? `Block ${block.id}`} (
                {block.type === ApiComponentType.LANDING_BLOCK
                  ? "Block"
                  : "Component"}
                )
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            size="small"
            label="Product source"
            value={productSource}
            onChange={(e) =>
              setProductSource(e.target.value as ProductSourceType)
            }
          >
            {PRODUCT_SOURCE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {productSource !== "manual" && (
            <Typography variant="body2" color="text.secondary">
              {productSource === "custom"
                ? "Choose your own weighted ranking metrics. Products are sorted by the calculated rank score."
                : TOP_PRODUCTS_STRATEGIES[
                    productSource as TopProductsStrategyKey
                  ].description}
            </Typography>
          )}

          {productSource !== "manual" && (
            <>
              <TextField
                select
                fullWidth
                size="small"
                label="Number of products"
                value={topN}
                onChange={(e) => setTopN(Number(e.target.value))}
                helperText="Fetched from analytics when you click Add grid"
              >
                {TOP_N_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label="Date range"
                value={lastNDays}
                onChange={(e) => setLastNDays(Number(e.target.value))}
                helperText="Period used for analytics"
              >
                {LAST_N_DAYS_OPTIONS.map((days) => (
                  <MenuItem key={days} value={days}>
                    Last {days} days
                  </MenuItem>
                ))}
              </TextField>
              {productSource === "custom" && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {customRanks.map((rank, idx) => (
                    <Box
                      key={`custom-rank-${idx}`}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.4fr 1fr 0.8fr",
                        gap: 1,
                      }}
                    >
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={`Rank ${idx + 1} metric`}
                        value={rank.metric}
                        onChange={(e) => {
                          const next = [...customRanks];
                          next[idx] = {
                            ...next[idx],
                            metric: e.target.value as ApiMetric,
                          };
                          setCustomRanks(next);
                        }}
                      >
                        {CUSTOM_TOP_PRODUCTS_METRIC_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label={`Rank ${idx + 1} direction`}
                        value={rank.direction}
                        onChange={(e) => {
                          const next = [...customRanks];
                          next[idx] = {
                            ...next[idx],
                            direction: e.target.value as ApiChartRankDrection,
                          };
                          setCustomRanks(next);
                        }}
                      >
                        <MenuItem value={ApiChartRankDrection.DESC}>
                          DESC
                        </MenuItem>
                        <MenuItem value={ApiChartRankDrection.ASC}>
                          ASC
                        </MenuItem>
                      </TextField>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={`Rank ${idx + 1} weight`}
                        value={rank.weight ?? 0}
                        onChange={(e) => {
                          const next = [...customRanks];
                          next[idx] = {
                            ...next[idx],
                            weight: Number(e.target.value),
                          };
                          setCustomRanks(next);
                        }}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}

          {productSource === "manual" && (
            <Autocomplete
              multiple
              options={products}
              getOptionLabel={(p) => p.name ?? ""}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              value={selectedProducts}
              onChange={(_, next) => setSelectedProducts(next)}
              loading={productsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Products"
                  size="small"
                  placeholder="Select products"
                />
              )}
            />
          )}

          <TextField
            select
            fullWidth
            size="small"
            label="Columns (desktop)"
            value={columnsDesktop}
            onChange={(e) => setColumnsDesktop(Number(e.target.value))}
            helperText="Mobile is always 1 column"
          >
            {COLUMNS_DESKTOP_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>
                {n} per row
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting ? "Adding…" : "Add grid"}
        </Button>
      </DialogActions>
    </CustomDialog>
  );
}
